import { useMemo } from 'react';
import PropTypes from 'prop-types';
import { isManualStep, getBranchActionSteps, walkCadenceGraph } from '../../utils/cadence-step-utils';

/**
 * Filter header for the Performance & Recipients card.
 * Provides step selection and timeframe dropdowns.
 * Uses graph walk for branch-aware step grouping in the dropdown.
 */
const StepFilterHeader = ({
  steps,
  cadence,
  selectedStepFilter,
  onStepFilterChange,
  timeframeFilter,
  onTimeframeChange,
}) => {
  const graphItems = useMemo(() => {
    if (!cadence?.events?.length) return null;
    const { items } = walkCadenceGraph(cadence.events, cadence.edges || []);
    return items;
  }, [cadence?.events, cadence?.edges]);

  const stepById = useMemo(() => {
    const map = {};
    for (const s of steps) map[String(s.id)] = s;
    return map;
  }, [steps]);

  const hasBranches = graphItems && graphItems.some((i) => i.type === 'branch');

  const renderStepOption = (item, step) => {
    const mode = step ? (isManualStep(step) ? 'Manual' : 'Automated') : item.mode || 'Automated';
    const typeLabel = step?.label || item.actionType || 'Email';
    const name = step?.name || item.name || '';
    const titleClause = name && name !== typeLabel ? ` - ${name}` : '';
    const idSuffix = step?.cadenceEventReferenceNumber != null ? ` (ID ${step.cadenceEventReferenceNumber})` : '';
    return (
      <option key={String(item.id)} value={String(item.id)}>
        {mode} - {typeLabel}
        {titleClause}
        {idSuffix}
      </option>
    );
  };

  return (
    <div className="cadence-detail-page--combined-header">
      <div className="cadence-detail-page--combined-title-area">
        <h2 className="cadence-detail-page--combined-title">Performance &amp; Recipients</h2>
        <p className="cadence-detail-page--combined-subtitle">
          Use the dropdowns below to view cadence metrics &amp; recipients
        </p>
      </div>
      <div className="cadence-detail-page--filter-controls">
        <div className="cadence-detail-page--filter-group">
          <label>View data for:</label>
          <select value={selectedStepFilter} onChange={(e) => onStepFilterChange(e.target.value)}>
            <option value="full_cadence">Full Cadence</option>
            {hasBranches
              ? graphItems.flatMap((item, idx) => {
                  if (item.type === 'step') {
                    const step = stepById[String(item.id)];
                    return [renderStepOption(item, step)];
                  }
                  if (item.type === 'branch') {
                    // Wait items share the branch arrays but are not selectable
                    // steps — skip them so the dropdown doesn't render a bogus
                    // "Automated - Email" entry that errors when selected.
                    const trueSteps = getBranchActionSteps(item.trueBranch);
                    const falseSteps = getBranchActionSteps(item.falseBranch);
                    const opts = [];
                    opts.push(
                      <option key={`branch-hdr-${idx}`} disabled style={{ fontWeight: 'bold', color: '#7c3aed' }}>
                        {'⑂ ' + item.conditionName}
                      </option>
                    );
                    if (trueSteps.length > 0) {
                      opts.push(
                        <option key={`branch-yes-${idx}`} disabled style={{ fontWeight: 600, color: '#15803d' }}>
                          {'  YES — ' + (item.condLabels?.yesHeader || 'True path')}
                        </option>
                      );
                      trueSteps.forEach((bs) => {
                        const step = stepById[String(bs.id)];
                        const mode = step ? (isManualStep(step) ? 'Manual' : 'Automated') : bs.mode || 'Automated';
                        const lbl = step?.label || bs.actionType || 'Email';
                        const nm = step?.name || bs.name || '';
                        const titleClause = nm && nm !== lbl ? ` - ${nm}` : '';
                        const idSuffix =
                          step?.cadenceEventReferenceNumber != null ? ` (ID ${step.cadenceEventReferenceNumber})` : '';
                        opts.push(
                          <option key={String(bs.id)} value={String(bs.id)}>
                            {'    ' + mode + ' - ' + lbl + titleClause + idSuffix}
                          </option>
                        );
                      });
                    }
                    if (falseSteps.length > 0) {
                      opts.push(
                        <option key={`branch-no-${idx}`} disabled style={{ fontWeight: 600, color: '#b91c1c' }}>
                          {'  NO — ' + (item.condLabels?.noHeader || 'False path')}
                        </option>
                      );
                      falseSteps.forEach((bs) => {
                        const step = stepById[String(bs.id)];
                        const mode = step ? (isManualStep(step) ? 'Manual' : 'Automated') : bs.mode || 'Automated';
                        const lbl = step?.label || bs.actionType || 'Email';
                        const nm = step?.name || bs.name || '';
                        const titleClause = nm && nm !== lbl ? ` - ${nm}` : '';
                        const idSuffix =
                          step?.cadenceEventReferenceNumber != null ? ` (ID ${step.cadenceEventReferenceNumber})` : '';
                        opts.push(
                          <option key={String(bs.id)} value={String(bs.id)}>
                            {'    ' + mode + ' - ' + lbl + titleClause + idSuffix}
                          </option>
                        );
                      });
                    }
                    return opts;
                  }
                  return [];
                })
              : steps.map((step) => {
                  const mode = isManualStep(step) ? 'Manual' : 'Automated';
                  const titleClause = step.name && step.name !== step.label ? ` - ${step.name}` : '';
                  const idSuffix =
                    step.cadenceEventReferenceNumber != null ? ` (ID ${step.cadenceEventReferenceNumber})` : '';
                  return (
                    <option key={String(step.id)} value={String(step.id)}>
                      {mode} - {step.label}
                      {titleClause}
                      {idSuffix}
                    </option>
                  );
                })}
          </select>
        </div>
        <div className="cadence-detail-page--filter-group">
          <label>Timeframe:</label>
          <select value={timeframeFilter} onChange={(e) => onTimeframeChange(e.target.value)}>
            <option value="1_week">In the last week</option>
            <option value="1_month">In the last month</option>
            <option value="3_months">In the last 3 months</option>
            <option value="6_months">In the last 6 months</option>
            <option value="12_months">In the last year</option>
            <option value="all_time">All time</option>
          </select>
        </div>
      </div>
    </div>
  );
};

StepFilterHeader.propTypes = {
  steps: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      label: PropTypes.string,
      name: PropTypes.string,
    })
  ).isRequired,
  cadence: PropTypes.object,
  selectedStepFilter: PropTypes.string.isRequired,
  onStepFilterChange: PropTypes.func.isRequired,
  timeframeFilter: PropTypes.string.isRequired,
  onTimeframeChange: PropTypes.func.isRequired,
};

export default StepFilterHeader;
